import { FC, ReactNode, useState, isValidElement, MouseEvent, useEffect, useMemo } from 'react';
import {
  useFloating,
  useInteractions,
  useHover,
  useClick,
  useDismiss,
  offset,
  flip,
  shift,
  Placement,
  autoUpdate,
  FloatingPortal,
  safePolygon,
  useRole,
  useFocus
} from '@floating-ui/react';
import './Popover.css';

interface PopoverProps {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  trigger?: 'click' | 'hover' | 'both';
  placement?: Placement;
  disabled?: boolean;
  closeOnClickOutside?: boolean;
  id?: string;
  stopPropagation?: boolean;
  hoverDelay?: { open: number; close: number };
}

const Popover: FC<PopoverProps> = ({ 
  children, 
  content, 
  className = '',
  trigger = 'click',
  placement = 'bottom',
  disabled = false,
  closeOnClickOutside = true,
  id,
  stopPropagation = true,
  hoverDelay = { open: 100, close: 200 }
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);

  const { refs, floatingStyles, context, placement: actualPlacement } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      shift()
    ]
  });

  // Calculate transform origin based on placement
  const transformOrigin = useMemo(() => {
    if (!actualPlacement) return 'top center';
    
    // Set the transform origin based on the actual placement
    if (actualPlacement.includes('top')) return 'bottom center';
    if (actualPlacement.includes('bottom')) return 'top center';
    if (actualPlacement.includes('left')) return 'right center';
    if (actualPlacement.includes('right')) return 'left center';
    
    return 'center center';
  }, [actualPlacement]);

  // Reset positioning state when popover closes
  useEffect(() => {
    if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen]);

  // Set positioned state after styles are calculated
  useEffect(() => {
    if (isOpen && floatingStyles.transform) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsPositioned(true);
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen, floatingStyles.transform]);

  const hover = useHover(context, {
    enabled: trigger === 'hover' || trigger === 'both',
    delay: hoverDelay,
    handleClose: safePolygon()
  });
  
  const click = useClick(context, {
    enabled: trigger === 'click' || trigger === 'both'
  });

  const dismiss = useDismiss(context, {
    enabled: closeOnClickOutside
  });
  
  const focus = useFocus(context);
  
  const role = useRole(context, {
    role: 'dialog'
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    focus,
    role
  ]);

  // Generate unique IDs for accessibility
  const popoverId = id || `popover-${Math.random().toString(36).substr(2, 9)}`;
  const contentId = `${popoverId}-content`;

  // Don't render if disabled
  if (disabled) {
    return <>{children}</>;
  }

  // Handle click with stopPropagation if needed
  const handleReferenceClick = (e: MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  // Get reference props with our custom click handler
  const referenceProps = getReferenceProps({
    'aria-expanded': isOpen,
    'aria-haspopup': true,
    'aria-controls': isOpen ? contentId : undefined,
    onClick: handleReferenceClick
  });

  // Clone the child element to add the necessary props
  let triggerElement;
  if (isValidElement(children)) {
    triggerElement = (
      <span ref={refs.setReference} {...referenceProps} style={{ display: 'inline-block' }}>
        {children}
      </span>
    );
  } else {
    triggerElement = (
      <span ref={refs.setReference} {...referenceProps} style={{ display: 'inline-block' }}>
        {children}
      </span>
    );
  }

  // Handle click on popover content to prevent bubbling
  const handleContentClick = (e: MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  return (
    <>
      {triggerElement}
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              // Only show the popover when it's positioned
              opacity: isPositioned ? 1 : 0,
              // Prevent interaction before positioned
              pointerEvents: isPositioned ? 'auto' : 'none',
              // Add transition for smooth appearance
              transition: 'opacity 0.15s ease-out',
              // Set transform origin based on placement
              '--floating-transform-origin': transformOrigin
            } as React.CSSProperties}
            className={`popover ${className}`}
            {...getFloatingProps()}
            id={contentId}
            role="tooltip"
            aria-hidden={!isOpen}
            onClick={handleContentClick}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default Popover; 